import { bigint, z, ZodType } from "zod";
import { InvoiceItem } from "~/onesign/invoice-totals";
import { Invoice } from "~/mydata";
import { timestamp } from "~/schemas";
import { invoiceSchema, supportedInvoiceTypes } from "~/schemas/invoices";
import { recipientEU, recipientGR, recipientNonEU } from "~/schemas/recipient";

export const markId = z.coerce.bigint().transform((mark) => mark.toString());
export const invoiceTypeEnum = z.enum(supportedInvoiceTypes);
export const invoiceDraft = z
  .object({
    invoiceType: invoiceTypeEnum,
    taxSeriesId: z.string().uuid().optional(),
  })
  .passthrough();

export const findOrCreateDraftInvoiceInput = z.object({
  issuerId: z.string().uuid(),
  draftInvoiceId: z.string().uuid(),
  invoiceType: z.enum(supportedInvoiceTypes),
});

export type InvoiceDraft = z.infer<typeof invoiceDraft>;
export const draftInvoice = z.object({
  id: z.string().uuid(),
  issuerId: z.string().uuid(),
  createdBy: z.string().uuid(),
  updatedBy: z.string().uuid(),
  createdAt: timestamp,
  updatedAt: timestamp,
  invoice: z
    .object({
      recipient: z.union([recipientGR, recipientEU, recipientNonEU]),
      items: z.array(z.object({}) as unknown as ZodType<InvoiceItem>),
    })
    .merge(invoiceDraft),
});
export type DraftInvoice = z.infer<typeof draftInvoice>;

export const saveDraftInvoiceInput = z.object({
  issuerId: z.string().uuid(),
  draftInvoiceId: z.string().uuid(),
  draft: invoiceDraft,
});

export const listDraftInvoicesInput = z.object({
  issuerId: z.string().uuid(),
  invoiceType: z.enum(supportedInvoiceTypes).optional(),
  taxSeriesId: z.string().uuid().optional(),
});

export const issueInvoiceInput = z.object({
  invoice: invoiceSchema,
  recipientEmailAddress: z.string().optional(),
  draftInvoiceId: z.string(),
});

//for invoices
export const invoiceData = z.object({
  issuedAt: timestamp,
  taxSeriesId: z.string().uuid(),
  taxSeriesNumber: z.number(),
  uid: z.string(),
  mark: markId.optional(),
  transmission_failure: z.enum(["myData", "offline"]).optional(),
  authentication_code: z.string().optional(),
  payload: z.string(),
  retries: z.number(),
  deliveredAt: timestamp,
  recipientId: z.string().uuid(),
});

export type InvoiceData = z.infer<typeof invoiceData>;

export const invoiceDataInput = z
  .object({
    uid: z.string().min(40),
    mark: z.bigint(),
    id: z.string().optional(),
  });

export const invoicesDataInput = z
  .object({
    vatNumber: z.string(),
    startDate: timestamp,
    endDate: timestamp,
  })
  .refine(
    (invoice) => {
      if (invoice.endDate < invoice.startDate) {
        return false;
      }
      return true;
    },
    {
      path: ["endDate"],
      message: "Η τελική ημερομηνία πρέπει να είναι μεγαλύτερη της αρχικής",
    }
  )
  .refine(
    (invoice) => {
      if (invoice.startDate > invoice.endDate) {
        return false;
      }

      return true;
    },
    {
      path: ["startDate"],
      message: "Η αρχική ημερομηνία πρέπει να είναι μικρότερη της τελικής",
    }
  );

export const issuerInvoicesDataInput = z
  .object({
    startDate: timestamp.optional(),
    endDate: timestamp.optional(),
  })
  .refine(
    (invoice) => {
      if (invoice.startDate && invoice.endDate) {
        if (invoice.endDate < invoice.startDate) {
          return false;
        }
      }
      return true;
    },
    {
      path: ["endDate"],
      message: "Η τελική ημερομηνία πρέπει να είναι μεγαλύτερη της αρχικής",
    }
  )
  .refine(
    (invoice) => {
      if (invoice.startDate && invoice.endDate) {
        if (invoice.startDate > invoice.endDate) {
          return false;
        }
      }

      return true;
    },
    {
      path: ["startDate"],
      message: "Η αρχική ημερομηνία πρέπει να είναι μικρότερη της τελικής",
    }
  );

export const invoiceDataOutput = z.object({
  mark: markId,
  id: z.string(),
  uid: z.string(),
  transmissionFailure: z.string(),
  payload: z.object({
    invoice: z.object({}) as unknown as ZodType<Invoice>,
    template: z.object({
      clientName: z.string(),
      clientVatNumber: z.string()
    }),
  }),
});

export type InvoiceDataOutput = z.infer<typeof invoiceDataOutput>;
export type IssuerInvoicesDataInput = z.infer<typeof issuerInvoicesDataInput>;
